import { CartComponentBase } from '@utilities/cart-component-base';
import { publish, EVENTS } from '@utilities/events';
import { parseConfig } from '@utilities/theme';

if (!customElements.get("cart-drawer")) {
  /**
   * Cart Drawer Web Component
   *
   * A custom element that manages the shopping cart drawer functionality,
   * including opening/closing, refreshing cart contents, and handling user interactions.
   *
   * @class CartDrawer
   * @extends CartComponentBase
   */
  class CartDrawer extends CartComponentBase {
    /** @type {boolean} Whether a template switch is currently in progress */
    #isTemplateSwitching = false;

    constructor() {
      super();
      /** @type {HTMLElement|null} Element that had focus before drawer opened */
      this.focusedElementBeforeOpen = null;
      /** @type {boolean} Whether the drawer is currently open */
      this.isOpen = false;

      /** @type {boolean} Whether cart state event listeners have been set up */
      this.hasCartStateListeners = false;
      /** @type {boolean} Whether delegated event listeners have been set up */
      this.hasDelegatedListeners = false;
      /** @type {boolean} Whether the drawer is in the process of opening */
      this.isOpening = false;

      // Bind methods to maintain context
      this.handleEscapeKey = this.handleEscapeKey.bind(this);
    }

    /**
     * Opens the cart drawer with focus management and scroll locking
     *
     * - Stores currently focused element for restoration on close
     * - Enables body scroll lock to prevent background scrolling
     * - Sets the 'open' attribute to trigger CSS animations
     * - Closes any open quick-add modals
     * - Traps focus within the drawer
     * - Always refreshes cart state to ensure up-to-date content
     */
    async open() {
      // Prevent opening if already open or in process of opening
      if (this.isOpen || this.isOpening) {
        if (this.allowDebug()) {
          console.log("CartDrawer: Drawer already open or opening, skipping", {
            isOpen: this.isOpen,
            isOpening: this.isOpening,
          });
        }
        return;
      }

      this.isOpening = true;

      if (this.allowDebug()) {
        console.log("CartDrawer: Opening drawer");
      }

      this.focusedElementBeforeOpen = document.activeElement;
      scrollLock.enabledBodyScrollLock();

      // Request animation frame for smoother animation process
      requestAnimationFrame(async () => {
        // Clear any inline styles that might interfere
        this.removeAttribute("style");
        this.setAttribute("open", "");
        this.isOpen = true;
        this.isOpening = false; // Clear opening flag

        // If quick view is open, close it
        if (document.querySelector("quick-add-modal[open]")) {
          document
            .querySelector("quick-add-modal[open]")
            .removeAttribute("open");
        }

        // Trap element focus within the drawer
        trapFocus(this);
        const firstFocusableElement = getFocusableElements(this)[0];
        if (firstFocusableElement) {
          firstFocusableElement.focus();
        }

        // Add escape key listener to close drawer
        document.addEventListener('keydown', this.handleEscapeKey);

        // Always refresh cart state on open to ensure up-to-date content
        if (this.allowDebug()) {
          console.log("CartDrawer: Refreshing cart state on open");
        }

        try {
          // Use CartStateManager to refresh - it will dispatch cart-state:changed if anything changed
          await this.refresh();
        } catch (error) {
          this._handleError(error, "refreshing cart state on open");
        }

        // Reset scroll position to top after drawer is fully opened and content is refreshed
        this.#scrollCartInnerToTop();

        publish(EVENTS.CART_DRAWER_OPENED, {
          drawer: this,
          state: this.cartStateManager?.getState()
        });

        if (this.allowDebug()) {
          console.log("CartDrawer: Drawer opened successfully");
        }
      });
    }

    /**
     * Refreshes the cart state
     * For backward compatibility with third-party integrations
     * @returns {Promise<void>}
     */
    async refresh() {
      try {
        await this.cartStateManager?.refreshState();
      } catch (error) {
        this._handleError(error, "refreshing cart state");
      }
    }

    /**
     * Closes the cart drawer and restores focus
     *
     * - Adds closing class for animations
     * - Disables body scroll lock
     * - Waits for animation to complete before removing open attribute
     */
    close() {
      if (this.allowDebug()) {
        console.log("CartDrawer: Closing drawer");
      }

      this.isOpen = false;
      this.isOpening = false; // Clear opening flag in case close is called during opening
      scrollLock.disableBodyScrollLock();

      // Remove escape key listener
      document.removeEventListener('keydown', this.handleEscapeKey);

      // Add closing class for reverse animation
      this.classList.add("closing");

      // Remove open attribute after animation duration
      const animationDuration = parseInt(
        getComputedStyle(this).getPropertyValue(
          "--cart-drawer-animation-duration"
        ) || "300", 10
      );

      // First remove the open attribute to start the closing animation
      this.removeAttribute("open");

      // Immediately blur any focused elements inside the drawer to prevent accessibility issues
      const activeElement = document.activeElement;
      if (activeElement && this.contains(activeElement)) {
        activeElement.blur();
      }

      // After animation completes, clean up
      setTimeout(() => {
        this.classList.remove("closing");

        removeTrapFocus();
        if (this.focusedElementBeforeOpen) {
          this.focusedElementBeforeOpen.focus();
        }

        if (this.allowDebug()) {
          console.log("CartDrawer: Drawer closed successfully");
        }
      }, animationDuration);

    }

    /**
     * Handles click events for drawer close actions
     * @param {Event} event - The click event
     * @param {boolean} shouldPreventDefault - Whether to prevent the default event behavior
     */
    handleClick(event, shouldPreventDefault) {
      if (shouldPreventDefault) event.preventDefault();
      event.stopPropagation();
      this.close();
    }

    /**
     * Handles escape key presses to close the drawer
     * @param {KeyboardEvent} event - The keyboard event
     */
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.isOpen) {
        if (this.allowDebug()) {
          console.log("CartDrawer: Escape key pressed, closing drawer");
        }
        this.close();
      }
    }

    /**
     * Handles cart-state:refreshed event to force full drawer UI refresh
     * Updates counts, totals, button states, and template switching
     * @param {Object} data - The cart state refreshed data
     * @private
     */
    #handleStateRefreshed(data) {
      if (this.allowDebug()) {
        console.log("CartDrawer: Received cart-state:refreshed event, forcing full UI refresh");
      }

      const state = data?.state;
      if (!state) {
        if (this.allowDebug()) {
          console.warn("CartDrawer: No state provided in cart-state:refreshed event");
        }
        return;
      }

      // Force a full UI refresh by calling updateFromState with no changes object
      // This will trigger all peripheral UI updates and template switching if needed
      this.#updateFromState(state, {});
    }

    /**
     * Handles delegated click events within the cart drawer
     * Uses flexible selectors that work regardless of DOM position
     * @param {Event} event - The click event
     * @private
     */
    #handleDelegatedClick(event) {
      // Use flexible selectors and data attributes for A/B test compatibility
      const target = event.target.closest(
        "[data-cart-close], [data-cart-continue], .cart-drawer__close-button, .cart-drawer__backdrop, .cart-drawer__continue-shopping-button"
      );

      if (target) {
        // All these elements should close the drawer
        this.handleClick(event, true);
      }
    }


    /**
     * Override the base class event listener setup
     * @protected
     */
    _setupEventListeners() {
      this.#setupCartStateEventListeners();
      this.#setupDelegatedEventListeners();
      this._subscribe(EVENTS.CART_STATE_REFRESHED, this.#handleStateRefreshed.bind(this));
    }

    /**
     * Sets up delegated event listeners for the cart drawer
     * Uses event delegation for better performance and dynamic content handling
     * @private
     */
    #setupDelegatedEventListeners() {
      if (this.hasDelegatedListeners) return;
      this.addEventListener("click", this.#handleDelegatedClick.bind(this));
      this.hasDelegatedListeners = true;

      if (this.allowDebug()) {
        console.log("CartDrawer: Delegated event listeners set up");
      }
    }


    /**
     * Perform template switch between empty state and items state
     * @param {string} targetState - 'items' or 'empty'
     * @param {number} itemCount - Current item count
     * @private
     */
    async #performTemplateSwitch(targetState, itemCount) {
      // Prevent race conditions during template switching
      if (this.#isTemplateSwitching) {
        if (this.allowDebug()) {
          console.log("CartDrawer: Template switch already in progress, ignoring");
        }
        return;
      }

      if (this.allowDebug()) {
        console.log("CartDrawer: Performing template switch", {
          targetState,
          itemCount,
          drawerId: this.id
        });
      }

      this.#isTemplateSwitching = true;
      try {
        const contentContainer = this.querySelector("[data-cart-drawer-content-template]");

        if (!contentContainer) {
          throw new Error("Content container [data-cart-drawer-content-template] not found");
        }

        if (targetState === 'items' && itemCount > 0) {
          if (!this.itemsTemplate) {
            throw new Error("Items template not found");
          }

          const itemsContent = this.itemsTemplate.content.cloneNode(true);
          contentContainer.replaceChildren(itemsContent);

          // Wait for DOM update
          await new Promise((resolve) => requestAnimationFrame(resolve));

          if (this.allowDebug()) {
            console.log("CartDrawer: Switched to items template");
          }
        } else if (targetState === 'empty') {
          if (!this.emptyStateTemplate) {
            throw new Error("Empty state template not found");
          }

          const emptyContent = this.emptyStateTemplate.content.cloneNode(true);
          contentContainer.replaceChildren(emptyContent);

          if (this.allowDebug()) {
            console.log("CartDrawer: Switched to empty state template");
          }
        }
      } catch (error) {
        this._handleError(error, 'template switching');
      } finally {
        this.#isTemplateSwitching = false;
      }
    }

    /**
     * Updates cart state CSS classes based on item count
     * @param {number} itemsInCart - Number of items in cart
     * @private
     */
    #updateCartStateClasses(itemsInCart) {
      if (itemsInCart > 0) {
        this.classList.remove("cart-drawer--no-items");
        this.classList.add("cart-drawer--has-items");
      } else {
        this.classList.remove("cart-drawer--has-items");
        this.classList.add("cart-drawer--no-items");
      }

      if (this.allowDebug()) {
        console.log("CartDrawer: Updated cart state classes", {
          itemsInCart,
          hasItems: this.classList.contains("cart-drawer--has-items"),
          noItems: this.classList.contains("cart-drawer--no-items"),
        });
      }
    }

    /**
     * Scrolls the cart drawer inner container to the top with smooth animation
     * Called when items are added or removed from the cart
     * @private
     */
    #scrollCartInnerToTop() {
      const cartInner = this.querySelector("[data-cart-inner]");
      if (cartInner) {
        cartInner.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        if (this.allowDebug()) {
          console.log("CartDrawer: Scrolled cart inner to top with smooth behavior");
        }
      }
    }

    /**
     * Processes URL hash to auto-open cart drawer
     *
     * - Checks if URL contains '#cart'
     * - Opens cart drawer if not already open
     * - Uses header cart trigger to maintain proper event flow
     * @private
     */
    #processUrl() {
      const href = window.location.href.split("#")[1];
      // If we are not at #cart, disregard and move on
      if (href !== "cart") return;

      const headerTrigger = document.querySelector("#site-header [data-cart-drawer-trigger]");
      // If we cannot find the cart trigger in the header, move on
      if (!headerTrigger) return;

      // If cart drawer isn't already open, trigger it
      if (!this.getAttribute("open")) {
        headerTrigger.click();
      }
    }

    /**
     * Sets up event listeners for cart drawer open triggers
     *
     * Adds click handlers to all elements with the [data-cart-drawer-trigger] attribute
     * Each trigger will open the active cart drawer when clicked
     * @private
     */
    #setupTriggers() {
      if (!this.openTriggers) return;

      this.openTriggers.forEach((trigger) => {
        trigger.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();

          // Open this drawer directly since we only have one CartDrawer
          this.open();
        });
      });
    }

    /**
     * Override base class element caching for drawer-specific elements
     *
     * Caches references to important DOM elements using flexible selectors:
     * - Cart trigger buttons
     * - Template elements for empty state and items
     * - Inner drawer container
     * - Cart-specific components (announcements, notes, GWP selector)
     * - New backdrop and container elements
     * Uses both data attributes and class selectors for A/B test compatibility
     *
     * Note: Does not call super._cacheElements() as drawer doesn't need base elements
     * @protected
     */
    _cacheElements() {
      this.openTriggers = document.querySelectorAll("[data-cart-drawer-trigger]");
      this.section = this.closest(`section.shopify-section`);

      // Template elements for state switching
      this.emptyStateTemplate = this.querySelector(
        "[data-empty-state-template]"
      );

      this.itemsTemplate = this.querySelector("[data-items-template]");

      // Use flexible selectors for main container elements
      this.container = this.querySelector("[data-cart-container]");
      this.backdrop = this.querySelector("[data-cart-backdrop]");
      this.inner = this.querySelector("[data-cart-inner]");

      // Cart components use standard selectors as they're custom elements
      this.cartAnnouncement = this.querySelector("cart-announcement");
      this.cartNote = this.querySelector("cart-note");
      this.cartGWPSelector = this.querySelector("cart-gwp-selector");
      this.cartDiscounts = this.querySelector("cart-discounts");

      if (this.allowDebug()) {
        console.log("CartDrawer: Elements cached", {
          hasSection: !!this.section,
          hasEmptyStateTemplate: !!this.emptyStateTemplate,
          hasItemsTemplate: !!this.itemsTemplate,
          hasContainer: !!this.container,
          hasBackdrop: !!this.backdrop,
          hasInner: !!this.inner,
          emptyTemplateSelector: "[data-empty-state-template]",
          itemsTemplateSelector: "[data-items-template]",
          foundEmptyTemplates: this.querySelectorAll(
            "[data-empty-state-template]"
          ).length,
          foundItemsTemplates: this.querySelectorAll("[data-items-template]")
            .length,
        });
      }
    }

    /**
     * Sets up event listeners for cart state changes
     *
     * Listens for cart state events and updates the UI accordingly:
     * - cart-state:changed - Updates UI when cart state changes
     * - cart-state:item:added - Opens drawer when items are added (if specified)
     *
     * @listens cart-state:changed
     * @listens cart-state:item:added
     * @private
     */
    #setupCartStateEventListeners() {
      // Prevent duplicate event listener setup
      if (this.hasCartStateListeners) {
        if (this.allowDebug()) {
          console.warn(
            "CartDrawer: Cart state event listeners already set up, skipping"
          );
        }
        return;
      }

      // Create bound handlers to enable proper cleanup
      this.cartStateChangedHandler = (data) => {
        if (this.allowDebug()) {
          console.log("CartDrawer: Received cart-state:changed event:", {
            hasState: !!data?.state,
            hasChanges: !!data?.changes,
            itemCount: data?.state?.itemCount || 0,
            changes: data?.changes,
            source: data?.source || 'unknown',
            forced: data?.forced || false
          });
        }
        this.#updateFromState(data.state);
      };

      this.cartItemAddedHandler = (data) => {
        if (this.allowDebug()) {
          console.log("CartDrawer: Received cart-state:item:added event:", {
            openDrawer: data?.openDrawer,
            itemCount: data?.state?.itemCount || 0,
          });
        }
        if (data && data.openDrawer === true) {
          this.open();
        } else if (this.isOpen) {
          this.#scrollCartInnerToTop();
        }
      };

      this.cartItemRemovedHandler = (data) => {
        if (this.allowDebug()) {
          console.log("CartDrawer: Received cart-state:item:removed event:", {
            itemCount: data?.state?.itemCount || 0,
          });
        }
        this.#scrollCartInnerToTop();
      };

      this._subscribe(EVENTS.CART_STATE_CHANGED, this.cartStateChangedHandler);
      this._subscribe(EVENTS.CART_STATE_ITEM_ADDED, this.cartItemAddedHandler);
      this._subscribe(EVENTS.CART_STATE_ITEM_REMOVED, this.cartItemRemovedHandler);

      this.hasCartStateListeners = true;

      if (this.allowDebug()) {
        console.log("CartDrawer: Cart state event listeners set up");
      }
    }

    /**
     * Updates the cart drawer UI based on cart state changes
     *
     * - Updates header count and totals
     * - Updates button states
     * - Requests template switches via events when needed
     * - Lets CartItems handle its own item updates via cart-state:changed events
     *
     * @param {Object} state - Current cart state
     * @private
     */
    async #updateFromState(state) {
      try {
        if (!state) {
          if (this.allowDebug()) {
            console.warn("CartDrawer: No state provided for update");
          }
          return;
        }

        // If we're already in the middle of a template switch, skip this update
        if (this.#isTemplateSwitching) {
          if (this.allowDebug()) {
            console.log(
              "CartDrawer: Template switch in progress, skipping redundant state update"
            );
          }
          return;
        }

        // Always update peripheral UI first (classes, buttons, totals)
        await this.#updatePeripheralUI(state);

        // Check if we need to switch templates (empty ↔ has-items)
        const needsTemplateSwitch = this.#checkIfTemplateSwitch(state.itemCount);

        if (needsTemplateSwitch) {
          const targetState = state.itemCount > 0 ? 'items' : 'empty';
          await this.#performTemplateSwitch(targetState, state.itemCount);
        }

        // CartItems components handle their own updates via cart-state:changed events
        // No need to manually refresh here - they listen for the same events we do
      } catch (error) {
        this._handleError(error, "updating cart drawer from state");
      }
    }

    /**
     * Updates peripheral UI elements (counts, totals, buttons) based on cart state
     * @param {Object} state - Current cart state
     * @private
     */
    async #updatePeripheralUI(state) {
      // Update cart state classes first
      this.#updateCartStateClasses(state.itemCount);

      // Update item count text
      this.#updateItemsCountText(state.itemCount);

      // Update totals display
      this.#updateTotalsDisplay(state.totalPriceMoney);
    }

    /**
     * Check if we need to switch between empty state and has-items templates
     * @param {number} currentItemCount - Current item count
     * @returns {boolean} True if template switch is needed
     * @private
     */
    #checkIfTemplateSwitch(currentItemCount) {
      const contentContainer = this.querySelector(
        "[data-cart-drawer-content-template]"
      );
      if (!contentContainer) {
        if (this.allowDebug()) {
          console.log(
            "CartDrawer: Content container [data-cart-drawer-content-template] not found during template switch check"
          );
        }
        return false;
      }

      // If the container is empty (no children), we always need to clone a template
      if (contentContainer.children.length === 0) {
        if (this.allowDebug()) {
          console.log("CartDrawer: Content container is empty, template switch needed");
        }
        return true;
      }

      // Check what template is currently active
      const hasItemsContainer = contentContainer.querySelector(
        ".cart-drawer__items-container"
      );
      const currentlyShowingItems = !!hasItemsContainer;
      const shouldShowItems = currentItemCount > 0;

      // Template switch needed if current state doesn't match what should be shown
      const needsSwitch = currentlyShowingItems !== shouldShowItems;

      if (this.allowDebug() && needsSwitch) {
        console.log("CartDrawer: Template switch needed", {
          currentlyShowingItems,
          shouldShowItems,
          currentItemCount,
        });
      }

      return needsSwitch;
    }

    /**
     * Updates the cart totals display using flexible selectors
     * @param {string} moneyString - HTML string representing the total price formatted as money
     * @private
     */
    #updateTotalsDisplay(moneyString) {
      // Use flexible selectors that work regardless of DOM position
      const cartTotalElements = this.querySelectorAll("[data-cart-total-price]");
      cartTotalElements.forEach((totalElement) => {
        totalElement.innerHTML = moneyString;
      });
    }

    /**
     * Updates the items count text using flexible selectors
     * @param {number} itemCount - Current item count
     * @private
     */
    #updateItemsCountText(itemCount) {
      // Use flexible selectors that work regardless of DOM position
      const countElements = this.querySelectorAll("[data-cart-item-counter]");
      const countTextElements = this.querySelectorAll("[data-cart-item-counter-text]");
      if(countElements.length > 0) {
        countElements.forEach((element) => {
          element.textContent = itemCount;
        });
      }
      if(countTextElements.length > 0) {
        countTextElements.forEach((element) => {
          element.textContent = itemCount > 1 ? 'items' : 'item';
        });
      }
    }

    /**
     * Checks if this cart drawer has content template to consume
     * @returns {boolean} Whether there's a content template available
     * @private
     */
    #hasContentTemplate() {
      const template = this.querySelector(
        "[data-cart-drawer-content-template]"
      );
      return !!(
        template &&
        template.content &&
        template.content.children.length > 0
      );
    }

    /**
     * Consumes the content template and injects it into the drawer
     * This allows the drawer to be initialized with minimal DOM and only
     * populate content when it becomes the active drawer
     *
     * Only consumes the main content template (data-cart-drawer-content-template).
     * Button templates and other templates remain as direct children for reuse.
     * @private
     */
    #consumeContentTemplate() {
      if (this.allowDebug()) {
        console.log("CartDrawer: Consuming content template", {
          drawerId: this.id,
        });
      }

      const template = this.querySelector(
        "[data-cart-drawer-content-template]"
      );
      if (!template || !template.content) {
        if (this.allowDebug()) {
          console.warn("CartDrawer: No content template found to consume");
        }
        return false;
      }

      // Clone the template content
      const templateContent = template.content.cloneNode(true);

      // Clear existing content (except templates and the main content template)
      Array.from(this.children).forEach((child) => {
        // Keep all template elements except the main content template
        if (
          child === template ||
          (child.tagName === "TEMPLATE" && child !== template)
        ) {
          if (child === template) {
            // This is the main content template - it will be removed after consumption
            return;
          }
          // Keep other templates (button templates, etc.)
          return;
        }
        // Remove non-template elements
        child.remove();
      });

      // Inject the template content
      this.appendChild(templateContent);

      // Remove the main content template since we've consumed it
      template.remove();

      if (this.allowDebug()) {
        console.log(
          "CartDrawer: Content template consumed successfully, button templates preserved"
        );
      }

      return true;
    }

    /**
     * Public method to check if drawer has content template
     * @returns {boolean} Whether there's a content template available
     */
    hasContentTemplate() {
      return this.#hasContentTemplate();
    }

    /**
     * Public method to consume content template and re-initialize
     * @returns {Promise<boolean>} Whether the activation was successful
     */
    async consumeContentTemplate() {
      if (this.allowDebug()) {
        console.log("CartDrawer: Activating drawer", {
          drawerId: this.id,
          hasTemplate: this.#hasContentTemplate(),
        });
      }

      try {
        // Step 1: Clean up existing state to prepare for re-initialization
        this.#cleanupForReactivation();

        // Step 2: Consume template if it exists and hasn't been consumed yet
        if (this.#hasContentTemplate()) {
          const success = this.#consumeContentTemplate();
          if (!success) {
            console.error("CartDrawer: Failed to consume content template");
            return false;
          }
          if (this.allowDebug()) {
            console.log("CartDrawer: Content template consumed");
          }
        }

        // Step 3: Re-initialize the drawer with current cart state
        await this.#reinitializeDrawer();

        if (this.allowDebug()) {
          console.log("CartDrawer: Drawer activation complete", {
            drawerId: this.id,
            hasStateManager: !!this.cartStateManager,
            hasConfig: !!this.config,
            itemCount: this.cartStateManager?.getState()?.itemCount || 0,
          });
        }

        return true;
      } catch (error) {
        this._handleError(error, "activating cart drawer");
        return false;
      }
    }

    /**
     * Clean up existing state to prepare for re-activation
     * @private
     */
    #cleanupForReactivation() {
      if (this.allowDebug()) {
        console.log("CartDrawer: Cleaning up for re-activation");
      }

      // Clean up existing event listeners to prevent duplicates
      if (this.hasCartStateListeners) {
        document.removeEventListener(
          "cart-state:changed",
          this.cartStateChangedHandler
        );
        document.removeEventListener(
          "cart-state:item:added",
          this.cartItemAddedHandler
        );
        document.removeEventListener(
          "cart-state:item:removed",
          this.cartItemRemovedHandler
        );
      }

      // Reset initialization flags so components can be re-initialized
      this.hasCartStateListeners = false;
      this.hasDelegatedListeners = false;

      // Clear existing references that will be refreshed
      this.config = null;
      // Note: stateManager is now accessed via this.cartStateManager from base class
    }

    /**
     * Re-initialize the drawer after template consumption or activation
     * @private
     */
    async #reinitializeDrawer() {
      if (this.allowDebug()) {
        console.log("CartDrawer: Re-initializing drawer");
      }

      // 1. Cache DOM elements (in case template was consumed)
      this._cacheElements();

      // 2. Parse configuration using DOMUtils
      try {
        this.config = parseConfig(this, this.allowDebug());
      } catch (error) {
        console.error(`CartDrawer: Error parsing config:`, error);
        this.config = {};
      }

      // 3. Set up state manager reference
      // Note: stateManager is now accessed via this.cartStateManager from base class

      // 4. Set up drawer functionality
      this.#setupTriggers();
      this._setupEventListeners();

      // 5. Update UI to match current cart state
      const currentState = this.cartStateManager?.getState();
      if (currentState) {
        this.#updateCartStateClasses(currentState.itemCount);
        this.#updateItemsCountText(currentState.itemCount);
        this.#updateTotalsDisplay(currentState.totalPriceMoney);
      }

      // 7. Set global reference for backward compatibility
      window.cartDrawer = this;
    }

    /**
     * Override the base class component-specific initialization
     * @protected
     */
    async _initializeComponent() {

      try {
        const hasTemplate = this.#hasContentTemplate();

        if (this.allowDebug()) {
          console.log("CartDrawer: Starting initialization", {
            drawerId: this.id,
            hasTemplate,
          });
        }

        if (hasTemplate) {
          this.#consumeContentTemplate();
        }

        this.#setupTriggers();
        this.#processUrl();
      } catch (error) {
        throw error;
      }

      // Set initial cart state classes and clone the appropriate template
      const initialState = this.cartStateManager?.getState();
      if (initialState) {
        this.#updateCartStateClasses(initialState.itemCount);

        // Clone the appropriate template into the body container on first load
        const targetState = initialState.itemCount > 0 ? 'items' : 'empty';
        await this.#performTemplateSwitch(targetState, initialState.itemCount);

        if (this.allowDebug()) {
          console.log("CartDrawer: Initial template cloned", {
            targetState,
            itemCount: initialState.itemCount,
          });
        }
      }

      // Set global reference for backward compatibility
      window.cartDrawer = this;

    }

    /**
     * Override base class cleanup to include component-specific cleanup
     * @protected
     */
    _cleanup() {
      if (this.hasCartStateListeners) {
        document.removeEventListener(
          "cart-state:changed",
          this.cartStateChangedHandler
        );
        document.removeEventListener(
          "cart-state:item:added",
          this.cartItemAddedHandler
        );
        document.removeEventListener(
          "cart-state:item:removed",
          this.cartItemRemovedHandler
        );
        this.hasCartStateListeners = false;
      }

      // Reset delegated listeners flag (listeners are removed automatically when element is removed)
      this.hasDelegatedListeners = false;

      // Remove escape key listener if it exists
      document.removeEventListener('keydown', this.handleEscapeKey);

      // Call base class cleanup
      super._cleanup();

      if (this.allowDebug()) {
        console.log(
          "CartDrawer: Cleaned up component-specific listeners and references"
        );
      }
    }

    /**
     * Override disconnectedCallback to handle cart drawer manager cleanup
     */
    disconnectedCallback() {
      if (this.allowDebug()) {
        console.log("CartDrawer: Disconnecting from DOM", {
          drawerId: this.id
        });
      }

      // No need to notify a manager since we only have one CartDrawer
      if (this.allowDebug()) {
        console.log("CartDrawer: Drawer being removed from DOM", {
          drawerId: this.id
        });
      }

      // Call parent disconnectedCallback
      super.disconnectedCallback();
    }
  }

  customElements.define("cart-drawer", CartDrawer);
}

if (!customElements.get("cart-sub-drawer")) {
  /**
   * CartSubDrawer Custom Web Component
   *
   * Provides a scalable foundation for sub-drawers within the cart drawer system.
   * This class handles layering, animations, focus management, and accessibility
   * for any cart-related sub-drawer functionality.
   *
   * Key Features:
   * - Proper z-index layering above cart drawer content
   * - Smooth slide-in/slide-out animations
   * - Focus trapping and restoration
   * - Event coordination with parent cart drawer
   * - Accessibility support (ARIA attributes, keyboard navigation)
   * - Backdrop handling
   * - Scroll lock management
   *
   * @class CartSubDrawer
   * @extends HTMLElement
   */
  class CartSubDrawer extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLElement|null} Element that had focus before sub-drawer opened */
      this.focusedElementBeforeOpen = null;
      /** @type {boolean} Whether the sub-drawer is currently open */
      this.isOpen = false;
      // Bind event handlers
      this.handleBackdropClick = this.handleBackdropClick.bind(this);
    }

    /**
     * Checks if debug mode is enabled by looking for debug class on parent cart components
     * @returns {boolean} Whether debug logging is enabled
     */
    allowDebug() {
      // Check if any parent cart component has debug class
      const cartComponent = this.closest('[class*="cart-"]');
      return cartComponent?.classList.contains("debug") || false;
    }



    /**
     * Called when element is added to DOM
     */
    connectedCallback() {
      this.setAttribute("role", "dialog");
      this.setAttribute("aria-modal", "true");
      this.setAttribute("aria-hidden", "true");
      this.className = "cart-sub-drawer";

      this.innerHTML = `
        <div class="cart-sub-drawer__backdrop"></div>
        <div class="cart-sub-drawer__container">
          <div class="cart-sub-drawer__content">
            <!-- Content will be set via setContent() -->
          </div>
        </div>
      `;

      this.backdrop = this.querySelector(".cart-sub-drawer__backdrop");
      this.container = this.querySelector(".cart-sub-drawer__container");
      this.content = this.querySelector(".cart-sub-drawer__content");

      // Add event listeners
      this.backdrop?.addEventListener("click", this.handleBackdropClick);
    }

    /**
     * Called when element is removed from DOM
     */
    disconnectedCallback() {
      // Use global removeTrapFocus if sub-drawer was open
      if (this.isOpen) {
        removeTrapFocus(this.focusedElementBeforeOpen);
      }
    }

    /**
     * Sets the content of the sub-drawer
     * @param {string} htmlContent - HTML content to display
     */
    setContent(htmlContent) {
      if (this.content) {
        this.content.innerHTML = htmlContent;
      }
    }

    /**
     * Shows the sub-drawer with animations and focus management
     */
    show() {
      if (this.isOpen) return;

      if (this.allowDebug()) {
        console.log("CartSubDrawer: Showing sub-drawer");
      }

      this.focusedElementBeforeOpen = document.activeElement;
      this.isOpen = true;

      // Show the sub-drawer
      this.removeAttribute("aria-hidden");
      this.setAttribute("open", "");

      // Use global trapFocus function
      trapFocus(this.container);
    }

    /**
     * Hides the sub-drawer with animations and focus restoration
     */
    hide() {
      if (!this.isOpen) return;

      if (this.allowDebug()) {
        console.log("CartSubDrawer: Hiding sub-drawer");
      }

      this.isOpen = false;

      // Use global removeTrapFocus function and restore focus
      removeTrapFocus(this.focusedElementBeforeOpen);

      // Hide the sub-drawer
      this.setAttribute("aria-hidden", "true");
      this.removeAttribute("open");

      // Dispatch close event
      this.dispatchEvent(
        new CustomEvent("sub-drawer:close", {
          bubbles: true,
          detail: { subDrawer: this },
        })
      );
    }

    /**
     * Handles backdrop clicks to close sub-drawer
     * @param {MouseEvent} event - The click event
     */
    handleBackdropClick(event) {
      if (event.target === this.backdrop) {
        this.hide();
      }
    }
  }

  customElements.define("cart-sub-drawer", CartSubDrawer);
}

// Debug helper for testing visibility changes
if (typeof window !== 'undefined') {
  window.debugCartVisibility = {
    // Test visibility change manually
    testVisibilityChange: () => {
      console.log('=== Testing visibility change manually ===');
      const stateManager = this.cartStateManager;
      if (stateManager) {
        stateManager.refreshState({
          source: 'manual-visibility-test',
          force: true
        }).then(() => {
          console.log('Manual visibility test completed');
        });
      } else {
        console.warn('Cart state manager not available');
      }
    },

    // Check current setup
    checkSetup: () => {
      console.log('=== Cart Visibility Setup Check ===');
      const drawer = document.querySelector('cart-drawer');
      console.log('Cart State Manager:', !!(drawer?.cartStateManager));
      console.log('Cart Drawer:', !!drawer);
      console.log('Cart Items:', !!document.querySelector('cart-items'));
      if (drawer) {
        console.log('Drawer is open:', drawer.isOpen);
      }
    }
  };
}
